import {
  Alert,
  AlertBody,
  AlertIcon,
  Button,
  cn,
  ComboBox,
  Divider,
  Item,
  Pill,
  RiArrowLeftLine,
  RiCloseLine,
  RiErrorWarningFill,
  RiErrorWarningLine,
  RiPencilLine,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Stack,
} from 'component-library';
import { ReactNode, useCallback, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePatchDraftPlot } from '@/api/rest/resources/plot';
import {
  CropsEnum,
  DraftPlot,
  LandManagementProcessEnum,
  MultiPolygon,
  PlotOwnershipEnum,
  PlotType,
} from '@/api/rest/resources/types/plot';
import { PlotTypeIcon } from '@/components';
import { getDraftPlotErrorTitleKey } from '@/utils/plot';

import { PLOT_LAND_TYPES } from '../constants';
import { useEditPlotForm } from '../hooks/useEditPlotForm';
import { usePlotLocation } from '../hooks/usePlotLocation';
import { DraftPlotBoundaryEditor } from './DraftPlotBoundaryEditor';
import { GroupPicker } from './GroupPicker';
import { LabelPicker } from './LabelPicker';

type InputMasterFieldProps = {
  label: string;
  hint?: string;
  children: ReactNode;
  className?: string;
};

const InputMasterField = ({ label, hint, children, className }: InputMasterFieldProps) => (
  <div className={cn('flex flex-col gap-0.5 rounded-xl border border-neutral-black-12 px-4 py-4', className)}>
    <span className='typography-overline text-text-secondary'>{label}</span>
    {children}
    {hint && <span className='typography-caption text-text-disabled'>{hint}</span>}
  </div>
);

type EditPlotPanelProps = {
  plot: DraftPlot;
  organizationId: string;
  hideBoundaryEdit?: boolean;
  onBack: () => void;
  onDelete: (id: string) => void;
  onSaved: (updated: DraftPlot) => void;
};

export const EditPlotPanel = ({
  plot,
  organizationId,
  hideBoundaryEdit,
  onBack,
  onDelete,
  onSaved,
}: EditPlotPanelProps) => {
  const { t } = useTranslation();
  const [saveError, setSaveError] = useState(false);
  const [showBoundaryEditor, setShowBoundaryEditor] = useState(false);
  const errorsSectionRef = useRef<HTMLDivElement>(null);
  const { mutateAsync: patchPlot, isPending: isBoundaryPending } = usePatchDraftPlot();

  const { form, handleSubmit, isSaving } = useEditPlotForm({
    plot,
    onSaved: (updated) => {
      setSaveError(false);
      onSaved(updated);
    },
  });

  const { control, setValue, watch } = form;
  const labelIds = watch('label_ids');
  const crops = watch('crops');
  const landType = watch('land_type');
  const areaHa = (plot.area / 10_000).toFixed(2);
  const hasBoundary = plot.multi_polygon !== null;
  const cropTypeMissing = landType === PlotType.CROPLAND && crops.length === 0;

  const cropTypeItems: Item[] = Object.values(CropsEnum).map((c) => ({ value: c, label: t(`global.crops.${c}`) }));
  const initialCropItems: Item[] = (crops ?? []).map((c) => ({ value: c, label: t(`global.crops.${c}`) }));

  const { location, isLoading: isLocationLoading } = usePlotLocation(plot.multi_polygon);

  const handleCropsChange = useCallback((values: string[]) => setValue('crops', values as CropsEnum[]), [setValue]);

  const handleBoundaryForward = async (multiPolygon: MultiPolygon) => {
    const updated = await patchPlot({ pathVariables: { id: plot.id }, bodyData: { multi_polygon: multiPolygon } });
    onSaved(updated);
    setShowBoundaryEditor(false);
  };

  if (showBoundaryEditor) {
    return (
      <DraftPlotBoundaryEditor
        draftPlot={plot}
        onForward={handleBoundaryForward}
        isForwardPending={isBoundaryPending}
        onBack={() => setShowBoundaryEditor(false)}
      />
    );
  }

  return (
    <div className='flex h-full flex-col overflow-hidden'>
      {/* Header */}
      <div className='flex shrink-0 items-center gap-2 border-b border-neutral-black-12 px-4 py-3'>
        <button
          type='button'
          aria-label={t('shared.uploadPlots.editPlot.backAriaLabel')}
          onClick={onBack}
          className='shrink-0 text-text-secondary hover:text-text-primary'
        >
          <RiArrowLeftLine size={18} />
        </button>
        <span className='typography-body1Semibold flex-1 truncate'>{plot.name}</span>
        {plot.errors.length > 0 && (
          <button
            type='button'
            onClick={() => errorsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            className='shrink-0'
          >
            <Pill size='small' className='flex items-center gap-1 bg-error-12 text-error'>
              <RiErrorWarningFill size={14} />
              {t('shared.plots.draftPlots.invalid')}
            </Pill>
          </button>
        )}
        <Pill size='small' className='shrink-0 bg-neutral-black-8 text-text-secondary'>
          {t('shared.uploadPlots.editPlot.draftBadge')}
        </Pill>
        <button
          type='button'
          aria-label={t('shared.uploadPlots.editPlot.deleteAriaLabel')}
          className='text-text-secondary hover:text-text-primary'
          onClick={() => onDelete(plot.id)}
        >
          <RiCloseLine size={18} />
        </button>
      </div>

      {/* Scrollable body */}
      <div className='flex-1 overflow-y-auto px-4 py-4'>
        <Stack spacing={6}>
          {saveError && (
            <Alert type='error'>
              <AlertIcon>
                <RiErrorWarningLine size={20} />
              </AlertIcon>
              <AlertBody>{t('shared.uploadPlots.editPlot.saveFailed')}</AlertBody>
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Plot Name */}
            <Controller
              control={control}
              name='name'
              rules={{ required: true }}
              render={({ field }) => (
                <InputMasterField
                  label={t('shared.uploadPlots.editPlot.fields.name')}
                  hint={t('shared.uploadPlots.editPlot.hints.detectedFromPlotData')}
                >
                  <input
                    {...field}
                    className='typography-body1 w-full bg-transparent outline-none placeholder:text-text-disabled'
                  />
                </InputMasterField>
              )}
            />

            {/* Land Use */}
            <Controller
              control={control}
              name='land_type'
              render={({ field }) => (
                <InputMasterField
                  label={t('shared.uploadPlots.editPlot.fields.landUse')}
                  hint={t('shared.uploadPlots.editPlot.hints.detectedFromPlotData')}
                >
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      className='border-none p-0 shadow-none'
                      leftAdornment={field.value ? <PlotTypeIcon type={field.value as PlotType} /> : undefined}
                      placeholder={t('shared.uploadPlots.editPlot.placeholders.landUse')}
                    />
                    <SelectContent>
                      {PLOT_LAND_TYPES.map((pt) => (
                        <SelectItem key={pt} value={pt} leftAdornment={<PlotTypeIcon type={pt} />}>
                          {t(`global.plotTypes.${pt}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </InputMasterField>
              )}
            />

            {/* Crop Type — required for Cropland; the backend wipes crops for every other land type */}
            <InputMasterField
              label={t('shared.uploadPlots.bulkEdit.editCropType')}
              className={cropTypeMissing ? 'border-error' : undefined}
            >
              <ComboBox
                data={cropTypeItems}
                initialSelectedItems={initialCropItems}
                placeholder={t('landSteward.plot.typeOfCrop.prompt_other', { count: 3 })}
                onChange={handleCropsChange}
              />
              {cropTypeMissing && (
                <span className='typography-caption text-error'>
                  {t('shared.uploadPlots.editPlot.errors.cropTypeRequired')}
                </span>
              )}
            </InputMasterField>

            {/* Land Steward */}
            <Controller
              control={control}
              name='land_steward_name'
              render={({ field }) => (
                <InputMasterField label={t('shared.uploadPlots.editPlot.fields.landSteward')}>
                  <input
                    {...field}
                    className='typography-body1 w-full bg-transparent outline-none placeholder:text-text-disabled'
                    placeholder={t('shared.uploadPlots.editPlot.placeholders.landSteward')}
                  />
                </InputMasterField>
              )}
            />

            {/* Land Management Process */}
            <Controller
              control={control}
              name='land_management_process'
              render={({ field }) => (
                <InputMasterField label={t('shared.uploadPlots.editPlot.fields.landManagement')}>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      className='border-none p-0 shadow-none'
                      placeholder={t('shared.uploadPlots.editPlot.placeholders.landManagement')}
                    />
                    <SelectContent>
                      {Object.values(LandManagementProcessEnum).map((lm) => (
                        <SelectItem key={lm} value={lm}>
                          {t(`shared.uploadPlots.landManagement.${lm}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </InputMasterField>
              )}
            />

            {/* Ownership */}
            <Controller
              control={control}
              name='ownership'
              render={({ field }) => (
                <InputMasterField label={t('shared.uploadPlots.editPlot.fields.ownership')}>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      className='border-none p-0 shadow-none'
                      placeholder={t('shared.uploadPlots.editPlot.placeholders.ownership')}
                    />
                    <SelectContent>
                      {Object.values(PlotOwnershipEnum).map((o) => (
                        <SelectItem key={o} value={o}>
                          {t(`shared.uploadPlots.ownership.${o}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </InputMasterField>
              )}
            />
          </Stack>

          {/* Read-only rows */}
          <div className='flex gap-6'>
            <div className='flex flex-col gap-0.5'>
              <span className='typography-overline text-text-secondary'>
                {t('shared.uploadPlots.editPlot.fields.plotArea')}
              </span>
              <span className='typography-body1'>{areaHa} ha</span>
            </div>
            <div className='flex flex-col gap-0.5'>
              <span className='typography-overline text-text-secondary'>
                {t('shared.uploadPlots.editPlot.fields.landscapeArea')}
              </span>
              <span className='typography-body1'>0 ha</span>
            </div>
          </div>

          <div className='flex flex-col gap-0.5'>
            <span className='typography-overline text-text-secondary'>
              {t('shared.uploadPlots.editPlot.fields.location')}
            </span>
            <span className='typography-body1'>
              {isLocationLoading ? '…' : (location ?? t('shared.uploadPlots.editPlot.placeholders.noLocation'))}
            </span>
          </div>

          <Divider />

          {/* Labels */}
          <Stack spacing={2}>
            <span className='typography-overline text-text-secondary'>
              {t('shared.uploadPlots.editPlot.sections.labels')}
            </span>
            <LabelPicker
              labelIds={labelIds}
              organizationId={organizationId}
              onAdd={(label) => setValue('label_ids', [...labelIds, label.id])}
              onRemove={(labelId) =>
                setValue(
                  'label_ids',
                  labelIds.filter((id) => id !== labelId),
                )
              }
            />
          </Stack>

          {/* Add to Group */}
          <Stack spacing={2}>
            <span className='typography-overline text-text-secondary'>
              {t('shared.uploadPlots.editPlot.sections.addToGroup')}
            </span>
            <GroupPicker plot={plot} organizationId={organizationId} />
          </Stack>

          {/* Boundaries — file-upload plots only */}
          {hasBoundary && !hideBoundaryEdit && (
            <Stack spacing={2}>
              <span className='typography-overline text-text-secondary'>
                {t('shared.uploadPlots.editPlot.sections.boundaries')}
              </span>
              <Button
                variant='outline'
                size='small'
                className='w-fit'
                leftAdornment={<RiPencilLine size={14} />}
                onClick={() => setShowBoundaryEditor(true)}
              >
                {t('shared.uploadPlots.editPlot.boundaries.edit')}
              </Button>
            </Stack>
          )}

          {plot.errors.length > 0 && (
            <Alert type='error' ref={errorsSectionRef}>
              <AlertIcon>
                <RiErrorWarningLine size={20} />
              </AlertIcon>
              <AlertBody>
                <Stack spacing={0.5}>
                  {plot.errors.map((error) => (
                    <span key={error}>{t(getDraftPlotErrorTitleKey(error))}</span>
                  ))}
                </Stack>
              </AlertBody>
            </Alert>
          )}
        </Stack>
      </div>

      {/* Footer */}
      <div className='flex shrink-0 items-center justify-between border-t border-neutral-black-12 px-4 py-3'>
        <Button variant='outline' size='small' onClick={onBack}>
          {t('global.ui.buttons.cancel')}
        </Button>
        <Button
          size='small'
          loading={isSaving}
          disabled={cropTypeMissing}
          onClick={() => {
            setSaveError(false);
            handleSubmit().catch(() => setSaveError(true));
          }}
        >
          {t('shared.uploadPlots.editPlot.saveButton')}
        </Button>
      </div>
    </div>
  );
};
